// pt-BR translations of the core English catalog (en.ts) that were still falling back to English (D36 i18n audit).
// The guard test `apps/web/test/i18n-guard.test.ts` keeps every English key translated from now on.
import type { MessageKey } from "./en.ts";

export const ptBRCore: Partial<Record<MessageKey, string>> = {
  "validation.email": "Digite um e-mail válido.",
  "validation.passwordLength": "Use pelo menos {n} caracteres.",
  "validation.passwordMatch": "As senhas não coincidem.",
  "validation.repository": "Use uma URL de repositório https://, ssh:// ou git@.",
  "validation.chooseProject": "Escolha um projeto.",

  "nav.main": "Navegação principal",
  "nav.sidebar": "Navegação",
  "nav.recentUnavailable": "Os projetos recentes não estão disponíveis agora.",
  "nav.resize": "Redimensionar navegação",
  "nav.workspaceSelector": "Espaço de trabalho: {name}",
  "nav.workspaceSwitch": "Trocar ou criar espaço de trabalho",
  "user.menu": "Menu do usuário",
  "user.menuFor": "Menu do usuário de {name}",
  "role.admin": "Administrador",
  "role.maintainer": "Mantenedor",
  "role.operator": "Operador",
  "role.reviewer": "Revisor",
  "role.observer": "Observador",
  "conn.region": "Status da conexão",
  "conn.componentsAttention": "{list} com problema",
  "conn.noDataYet": "nenhum dado ao vivo recebido ainda",
  "conn.staleNoticeNoTime": "As atualizações ao vivo foram interrompidas. Os dados desta página podem estar desatualizados.",
  "realtime.taskMoved": "A tarefa #{number} agora está em {status}.",

  "auth.email": "E-mail",
  "auth.password": "Senha",
  "auth.signIn": "Entrar",
  "auth.signInTitle": "Entrar no Primage Studio",
  "auth.signInSubtitle": "Use a conta da sua organização.",
  "auth.invalidCredentials": "E-mail ou senha incorretos.",
  "auth.unreachableTitle": "Não foi possível acessar a API",
  "auth.unreachableSubtitle": "O painel não conseguiu falar com o servidor. Seu trabalho continua rodando no backend; só esta tela foi afetada.",
  "setup.title": "Configurar o Primage Studio",
  "setup.subtitle": "Crie a organização e o primeiro administrador.",
  "setup.organization": "Nome da organização",
  "setup.displayName": "Seu nome",
  "setup.confirmPassword": "Confirme a senha",
  "setup.passwordHint": "Pelo menos 12 caracteres. Ela é enviada uma vez pela conexão e nunca fica guardada no navegador.",
  "setup.mfaNext": "Em seguida você vai cadastrar um aplicativo autenticador: a autenticação em dois fatores é obrigatória para administradores e mantenedores.",
  "setup.submit": "Criar administrador",
  "setup.failed": "A configuração falhou",
  "setup.alreadyDone": "Esta instalação já está configurada",
  "setup.haveAccount": "Já tenho uma conta — entrar",
  "auth.firstRun": "Primeiro acesso? Configure a organização",

  "home.inputLabel": "Descreva o que você quer construir",
  "home.placeholder": "Por exemplo: um sistema de estoque integrado aos meus marketplaces…",
  "home.removeAttachment": "Remover o anexo {name}",
  "home.destination": "Destino",
  "home.existingProjects": "Projetos existentes",
  "home.destinationNew": "Vai criar um projeto novo:",
  "home.projectNamePlaceholder": "Nome do projeto",
  "home.destinationAutonomy": "autonomia: {preset}",
  "home.destinationExisting": "Vai importar para o projeto existente “{name}”.",
  "home.autonomyLabel": "Nível de autonomia do novo projeto",
  "home.autonomyHint": "Vale para o projeto criado a partir desta mensagem; dá para mudar depois nas configurações do projeto.",
  "home.validationEmpty": "Descreva o que você quer construir ou anexe um PRD.",
  "home.validationChatLike": "Isso parece uma mensagem de conversa, não um briefing de projeto. Descreva o que construir ou mude para Conversar para fazer uma pergunta.",
  "home.validationName": "Dê um nome ao novo projeto.",
  "home.validationDestination": "O projeto escolhido não está mais disponível. Escolha outro destino.",
  "home.submitFailed": "Seu pedido não foi concluído",
  "home.draftKept": "Seu texto e o destino foram mantidos. Tentar de novo reaproveita as mesmas chaves de requisição, então nenhum projeto ou importação é duplicado.",
  "home.created": "O projeto “{name}” está pronto",
  "home.openProject": "Abrir projeto",
  "home.openKanban": "Abrir Kanban",
  "home.reviewPending": "Revisar a cobertura do PRD",
  "home.suggestions": "Outras formas de começar",
  "home.emptyBody": "Comece descrevendo uma ideia acima, anexando um PRD ou criando um projeto. Para os agentes rodarem código, configure sua infraestrutura antes.",

  "projects.search": "Buscar projetos",
  "projects.filters": "Filtrar projetos",
  "projects.countLabel": "{n} projetos",
  "projects.viewMode": "Modo de visualização",
  "projects.viewGrid": "Visualização em grade",
  "projects.viewList": "Visualização em lista",
  "projects.listLabel": "Projetos",
  "projects.noDescription": "Sem descrição.",
  "projects.lastActivity": "Última atividade {when}",
  "projects.noActivity": "Nenhuma atividade ainda",
  "projects.progressShort": "{done}/{total} tarefas obrigatórias aceitas",
  "projects.scope": "Escopo: {scope}",
  "projects.noRequiredTasks": "Nenhuma tarefa obrigatória ainda",
  "projects.activeAgents": "{n} agentes ativos",
  "projects.loadMore": "Carregar mais projetos",
  "projects.filterLoadedOnly": "O filtro vale para os projetos carregados até agora.",
  "projects.loadFailed": "Não foi possível carregar os projetos",
  "projects.emptyTitle": "Nenhum projeto ainda",
  "projects.emptyBody": "Crie um projeto ou descreva uma ideia no Início para criar um com PRD e backlog.",
  "projects.startFromHome": "Descrever uma ideia",
  "projects.noMatchTitle": "Nenhum projeto encontrado",
  "projects.noMatchBody": "Tente outro termo de busca ou outro filtro.",
  "projects.clearFilters": "Limpar filtros",
  "projectState.draft": "Rascunho",
  "projectState.active": "Ativo",
  "projectState.paused": "Pausado",
  "projectState.draining": "Finalizando",
  "projectState.stopped": "Parado",

  "createProject.title": "Novo projeto",
  "createProject.description": "O projeto delimita tarefas, PRDs, memória e acesso.",
  "createProject.importTitle": "Importar repositório",
  "createProject.importDescription": "Crie um projeto ligado a um repositório existente. O acesso é conferido pela verificação de prontidão; nada é clonado ainda.",
  "createProject.repositoryHint": "O acesso ao repositório é conferido depois, pela verificação de prontidão.",
  "createProject.autonomyHint": "Observação nunca altera código. Você pode aumentar a autonomia depois.",
  "createProject.submit": "Criar projeto",
  "createProject.failed": "O projeto não foi criado",
  "createProject.created": "Projeto “{name}” criado.",
  "project.name": "Nome",
  "project.description": "Descrição",
  "project.repository": "URL do repositório",
  "project.baseBranch": "Branch base",
  "project.deliveryTarget": "Forma de entrega",
  "project.autonomy": "Nível de autonomia",
  "project.maxConcurrent": "Máximo de tarefas simultâneas",
  "project.timezone": "Fuso horário de exibição",
  "project.autoExecute": "Iniciar tarefas prontas automaticamente",
  "project.autoExecuteHint": "Quando ligado, as tarefas prontas são admitidas sem confirmação card a card, dentro da política.",
  "project.breadcrumb": "Trilha de navegação",
  "project.sections": "Seções do projeto",
  "project.executionUnavailable": "O controle de execução fica indisponível enquanto o projeto está parando ou parado.",
  "project.loadFailed": "Não foi possível carregar o projeto",
  "project.notFound": "Projeto não encontrado ou sem acesso",
  "project.tab.prds": "PRDs",
  "project.code.title": "Código — ainda não disponível",
  "project.code.body": "A inspeção de arquivos e diffs por versão chega com o motor de execução, numa fase seguinte.",
  "project.runs.title": "Execuções — ainda não disponível",
  "project.runs.body": "Execuções, tentativas e logs completos aparecem aqui quando o motor de execução for ligado, numa fase seguinte.",

  "action.start_analysis": "Iniciar análise",
  "action.mark_ready": "Marcar como pronta",
  "action.start": "Iniciar",
  "action.submit_for_testing": "Enviar para teste",
  "action.gates_passed": "Verificações aprovadas",
  "action.gates_failed": "Verificações reprovadas",
  "action.review_approved": "Aprovar revisão",
  "action.review_rejected": "Rejeitar revisão",
  "action.integrated": "Marcar como integrada",
  "action.release_confirmed": "Confirmar liberação",
  "action.complete": "Concluir",
  "action.block": "Bloquear",
  "action.unblock": "Desbloquear",
  "action.wait_resource": "Aguardar recurso",
  "action.resource_available": "Recurso disponível",
  "action.pause": "Pausar",
  "action.resume": "Retomar",
  "action.fail": "Marcar como falha",
  "action.retry": "Tentar de novo",
  "action.cancel": "Cancelar tarefa",
  "action.reopen": "Reabrir",
  "action.admin_override": "Intervenção administrativa",
  "action.await_human": "Pedir a uma pessoa",
  "action.human_approved": "Aprovada por uma pessoa",
  "action.human_rejected": "Rejeitada por uma pessoa",
  "action.mark_existing": "Encerrar como já implementada",
  "taskType.feature": "Funcionalidade",
  "taskType.bugfix": "Correção",
  "taskType.investigation": "Investigação",
  "taskType.refactor": "Refatoração",
  "taskType.test": "Teste",
  "taskType.documentation": "Documentação",
  "taskType.operation": "Operações",
  "risk.low": "Risco baixo",
  "risk.medium": "Risco médio",
  "risk.high": "Risco alto",
  "risk.critical": "Risco crítico",
  "origin.manual": "Manual",
  "origin.prd": "PRD",
  "origin.chat": "Chat",
  "origin.api": "API",
  "origin.github": "GitHub",
  "origin.sentry": "Sentry",
  "origin.system": "Sistema",
  "delivery.local_commit": "Entrega: commit local",
  "delivery.pull_request": "Entrega: pull request",
  "delivery.merge_confirmed": "Entrega: merge confirmado",
  "delivery.deployment_validated": "Entrega: deploy validado",
  "autonomy.observation": "Observação",
  "autonomy.autonomous_development": "Desenvolvimento autônomo",
  "autonomy.autonomous_staging": "Staging autônomo",
  "autonomy.restricted_production": "Produção restrita",
  "autonomy.autonomous_admin_isolated_dev": "Administrador autônomo — desenvolvimento isolado",
  "durations.wall": "Tempo nos estados {value}",
  "durations.effort": "Soma dos intervalos de estado {value}",
  "durations.running": "intervalo de estado aberto",

  "kanban.board": "Quadro Kanban",
  "kanban.sideLane": "Estados laterais",
  "kanban.search": "Filtrar tarefas",
  "kanban.newTask": "Nova tarefa",
  "kanban.hint": "Arraste um card para pedir a mudança ou use o menu de ações dele. Quem decide é o servidor; as recusas mostram o motivo.",
  "kanban.actionsFor": "Ações da tarefa #{number}",
  "kanban.requestAction": "Pedir uma ação",
  "kanban.suggestedActions": "Ações sugeridas (o servidor decide)",
  "kanban.noActions": "Nenhuma ação disponível",
  "kanban.accepted": "A tarefa #{number} foi para {status}.",
  "kanban.refused": "Tarefa #{number}: “{action}” foi recusada",
  "kanban.refusedAnnounce": "Mudança da tarefa #{number} recusada: {reason}",
  "kanban.moveNotPossible": "A tarefa #{number} não pode ir para lá",
  "kanban.noActionForMove": "Nenhuma ação leva uma tarefa de {from} para {to}. Use o menu de ações para ver o que é possível.",
  "kanban.pendingGates": "Verificações pendentes:",
  "kanban.prdVersionOption": "Versão {n} do PRD",
  "kanban.emptyTitle": "Nenhuma tarefa ainda",
  "kanban.emptyBody": "Importe um PRD para planejar o backlog ou crie uma tarefa.",
  "kanban.confirmTitle": "{action} — tarefa #{number}",
  "kanban.confirmCancelBody": "Cancelar mantém todo o histórico; a tarefa pode ser reaberta depois.",
  "kanban.confirmFailBody": "Marcar como falha mantém o histórico de tentativas; a tarefa pode ser tentada de novo.",
  "kanban.reason": "Motivo",
  "kanban.serverDecides": "O servidor valida todo pedido (verificações, dependências e versão).",

  "prdImport.title": "Importar PRD",
  "prdImport.newVersionTitle": "Nova versão de “{title}”",
  "prdImport.description": "Cole o texto ou anexe Markdown, TXT, PDF ou DOCX. Macros e scripts nunca são executados.",
  "prdImport.docTitle": "Título do documento",
  "prdImport.source": "Origem",
  "prdImport.chooseFile": "Escolher arquivo",
  "prdImport.formats": "Markdown, TXT, PDF ou DOCX · até 10 MB",
  "prdImport.pasteText": "Ou cole o texto",
  "prdImport.startExecution": "Iniciar a execução depois da importação",
  "prdImport.startExecutionHint": "Deixe desmarcado para importar e planejar sem iniciar nenhuma tarefa.",
  "prdImport.submit": "Importar",
  "prdImport.failed": "A importação falhou",
  "prdImport.retryHint": "Tentar de novo com o mesmo conteúdo reaproveita a mesma chave de requisição e nunca duplica a importação.",
  "prdImport.missing": "Adicione um título e algum conteúdo.",
  "prdImport.unsupported": "Tipo de arquivo não suportado. Use Markdown, TXT, PDF ou DOCX.",
  "prdImport.tooLarge": "O arquivo passa de 10 MB.",
  "prdImport.empty": "O arquivo está vazio.",
  "prdImport.readFailed": "Não foi possível ler o arquivo. Tente de novo ou cole o texto.",
  "prdImport.reading": "Lendo o arquivo… {pct}%",
  "prdImport.uploading": "Enviando… {pct}%",
  "prdImport.extracting": "Extraindo os requisitos…",
  "prdImport.version": "Versão {n}",
  "prdImport.replayed": "Importação anterior devolvida",
  "prdImport.counts": "{requirements} requisitos · {tasks} tarefas · {pending} itens pendentes",
  "prdImport.duplicateWarning": "O mesmo conteúdo já foi importado antes. Esta importação foi mantida como uma nova versão intencional.",
  "extraction.complete": "Extração concluída",
  "extraction.partial": "Extração parcial",
  "extraction.failed": "A extração falhou",
  "extraction.unsupported": "Documento não suportado",
  "requirement.pending": "Pendente",
  "requirement.planned": "Planejado",
  "requirement.covered": "Coberto",
  "requirement.ambiguous": "Ambíguo",
  "requirement.not_implementable": "Não implementável",
  "requirement.superseded": "Substituído",
  "prds.intro": "Documentos versionados, requisitos extraídos e a cobertura deles.",
  "prds.import": "Importar PRD",
  "prds.newVersion": "Nova versão",
  "prds.version": "Versão",
  "prds.versionOption": "v{n} · {date}",
  "prds.noVersions": "Nenhuma versão ainda.",
  "prds.emptyTitle": "Nenhum PRD neste projeto",
  "prds.emptyBody": "Importe um PRD para extrair os requisitos e planejar as tarefas. Nenhum requisito é descartado em silêncio.",
  "prds.coverageSummary": "{total} requisitos · {open} precisam de atenção",
  "prds.onlyOpen": "Mostrar só pendentes ou ambíguos",
  "prds.noRequirements": "Nenhum requisito foi extraído desta versão.",
  "prds.nothingOpen": "Nenhum requisito pendente ou ambíguo.",
  "prds.coverageCaption": "Matriz de cobertura: requisito, estado, tarefas, testes e evidências",
  "prds.requirement": "Requisito",
  "prds.state": "Estado",
  "prds.tasks": "Tarefas",
  "prds.tests": "Testes",
  "prds.evidence": "Evidências",
  "prds.none": "Nenhum ainda",
  "prds.noTasks": "Nenhuma tarefa ainda",
  "prds.ambiguity": "Ambiguidade",
  "prds.optionalReq": "opcional",
  "prdsIndex.subtitle": "Os PRDs pertencem a projetos. Escolha um projeto para ver os documentos e a cobertura dele.",
  "prdsIndex.emptyTitle": "Nenhum PRD ainda",
  "prdsIndex.emptyBody": "Descreva uma ideia ou anexe um PRD no Início para criar um projeto com o PRD dele.",
  "prdsIndex.startHome": "Ir para o Início",

  "projectSettings.title": "Configurações do projeto",
  "projectSettings.saved": "Configurações do projeto salvas.",
  "projectSettings.conflictTitle": "Este projeto mudou desde que você o abriu",
  "projectSettings.conflictBody": "Outra pessoa salvou alterações. Carregue a versão mais recente e aplique suas edições de novo.",
  "projectSettings.loadLatest": "Carregar a mais recente",
  "projectSettings.version": "Versão {n}",
  "projectSettings.readiness": "Verificação de prontidão",
  "projectSettings.readinessBody": "Confere repositório, branch, workspace, runner, executor, comando de teste e rota de modelo antes da ativação.",
  "projectSettings.runReadiness": "Rodar verificação",
  "projectSettings.operationStatus": "Status da verificação:",
  "projectSettings.environments": "Workspaces, ambientes e acesso",
  "projectSettings.environmentsBody": "O cadastro de workspaces, ambientes e credenciais por projeto chega com a integração de infraestrutura.",
  "operation.accepted": "Aceita",
  "operation.running": "Em andamento",
  "operation.succeeded": "Concluída",
  "operation.failed": "Falhou",

  "settings.subtitle": "Infraestrutura, provedores, modelos, agentes, cotas e políticas.",
  "settings.structureOnly": "Esta seção mostra só a estrutura da configuração. Ela será ligada junto com a integração; nenhum resultado de teste aqui é simulado.",
  "settings.noSimulatedTests": "Os testes de conexão, inferência e capacidade só rodam contra serviços reais e configurados.",
  "settings.secret.notSet": "Não definido",
  "settings.secret.writeOnly": "Somente escrita: depois de salvar, só aparece um estado mascarado.",
  "settings.infra.intro": "VPS, SSH, Coolify e runners.",
  "settings.infra.unavailable": "A integração de infraestrutura não está ligada nesta versão. Os campos mostram a estrutura final; nada do que for digitado aqui é salvo ou enviado.",
  "settings.infra.actionsUnavailable": "Disponível quando a integração de infraestrutura for ligada.",
  "settings.infra.identification": "Identificação",
  "settings.infra.vpsName": "Nome da VPS",
  "settings.infra.purpose": "Finalidade",
  "settings.infra.purposeDev": "Desenvolvimento e testes",
  "settings.infra.provider": "Provedor",
  "settings.infra.system": "Sistema e arquitetura declarados",
  "settings.infra.ssh": "SSH",
  "settings.infra.host": "IP ou hostname",
  "settings.infra.port": "Porta",
  "settings.infra.user": "Usuário",
  "settings.infra.authMethod": "Método de autenticação",
  "settings.infra.privateKey": "Chave privada",
  "settings.infra.password": "Senha",
  "settings.infra.secrets": "Segredos do SSH",
  "settings.infra.secretsHint": "Guardados no cofre por referência. Senha e chave nunca são exigidas ao mesmo tempo.",
  "settings.infra.sshPassword": "Senha do SSH",
  "settings.infra.privateKeyField": "Chave privada",
  "settings.infra.passphrase": "Senha da chave",
  "settings.infra.sudoMode": "Modo de elevação",
  "settings.infra.sudoRoot": "Root",
  "settings.infra.sudoPasswordless": "Sudo sem senha",
  "settings.infra.sudoWithPassword": "Sudo com senha própria",
  "settings.infra.sudoPassword": "Senha do sudo",
  "settings.infra.identity": "Identidade do servidor",
  "settings.infra.identityHint": "A impressão digital da chave do host é registrada na primeira validação. Uma mudança bloqueia a conexão até a identidade ser revisada.",
  "settings.infra.fingerprint": "Impressão digital observada da chave do host",
  "settings.infra.fingerprintNone": "Ainda não validada",
  "settings.infra.coolifyApi": "API do Coolify",
  "settings.infra.coolifyUrl": "URL base do Coolify",
  "settings.infra.coolifyToken": "Token da API do Coolify",
  "settings.infra.coolifyWeb": "Login web opcional do Coolify",
  "settings.infra.coolifyWebHint": "Só é necessário para navegação autenticada. Nunca substitui o token da API.",
  "settings.infra.coolifyWebEnable": "Usar acesso web autenticado",
  "settings.infra.coolifyLoginUrl": "URL de login",
  "settings.infra.coolifyUser": "Usuário ou e-mail",
  "settings.infra.coolifyPassword": "Senha do painel do Coolify",
  "settings.infra.runner": "Runner e workspace",
  "settings.infra.runnerId": "Identidade do runner",
  "settings.infra.workdir": "Diretório de trabalho",
  "settings.infra.operation": "Operação",
  "settings.infra.limits": "Limites de CPU / RAM / disco / concorrência",
  "settings.infra.limitsPlaceholder": "Definido depois do inventário de capacidade",
  "settings.infra.testSsh": "Testar SSH",
  "settings.infra.testCoolify": "Testar Coolify",
  "settings.infra.verifyAdmin": "Verificar capacidade de administrador",
  "settings.infra.prepareRunner": "Preparar runner",
  "settings.providers.body": "OmniRoute, OpenRouter direto, NVIDIA NIM direto e o endpoint Jev aprovado.",
  "settings.providers.g1": "Conexões: nome, endpoint, autenticação e referência do segredo",
  "settings.providers.g2": "Chaves: adicionar, substituir, testar e revogar (somente escrita)",
  "settings.providers.g3": "Testes de conexão, de inferência mínima e de capacidade",
  "settings.models.body": "Catálogo quando houver, cadastro manual e IDs externos exatos.",
  "settings.models.g1": "Alvos de modelo: ID externo, apelido e tipo",
  "settings.models.g2": "Capacidades e limites declarados x verificados",
  "settings.models.g3": "Renomear sem perder o histórico",
  "settings.agents.body": "Cada perfil de agente com papel, modelo principal, alternativas e revisor.",
  "settings.agents.g1": "Matriz de modelo por papel",
  "settings.agents.g2": "Perfis de agente e ferramentas",
  "settings.agents.g3": "Maestro e substitutos fortes",
  "settings.quotas.body": "Pools compartilhados, RPM/TPM, concorrência e tetos de gasto.",
  "settings.quotas.g1": "Pools e janelas de cota",
  "settings.quotas.g2": "Tetos de orçamento e renovação",
  "settings.quotas.g3": "Comportamento quando falta um recurso",
  "settings.context.body": "Janela de contexto, reserva de saída, compactador, embedding, reranker e política de dados.",
  "settings.context.g1": "Janela e reserva de saída",
  "settings.context.g2": "Compactador, embedding e reranker",
  "settings.context.g3": "Política de dados por provedor",
  "settings.gauntlet.body": "Modo, construtor/crítico, referências, limites, detecção de estagnação e escalonamento.",
  "settings.gauntlet.g1": "Construtor e crítico",
  "settings.gauntlet.g2": "Limite de rodadas e estagnação",
  "settings.gauntlet.g3": "Regras de escalonamento",
  "settings.history.body": "Autor, diferença entre versões, testes de configuração, ativação e reversão.",
  "settings.history.g1": "Versões da configuração",
  "settings.history.g2": "Ativação e reversão",
  "settings.history.g3": "Trilha de auditoria",
  "settings.permissions.body": "Papéis, escopos e quem pode gerenciar conexões e segredos.",
  "settings.permissions.g1": "Membros e papéis",
  "settings.permissions.g2": "Contas de serviço",
  "settings.permissions.g3": "Política de acesso a segredos",
  "runs.heading": "As execuções chegam com o motor de execução",
  "runs.body": "Quando um executor estiver configurado, cada tentativa, seus passos, durações e evidências vão aparecer aqui.",
  "observability.heading": "Os painéis de observabilidade ainda não estão disponíveis",
  "observability.body": "A saúde da API mostrada na barra superior é ao vivo. Logs, rastreamentos, gargalos e alertas chegam numa fase seguinte.",
  "agents.subtitle": "Capacidades, qualidade medida, conexões e cotas.",
  "phase.f03": "Previsto para a fase F03 — execução.",
  "phase.f04": "Previsto para a fase F04 — configuração, modelos e cotas.",
  "phase.f08": "Previsto para a fase F08 — operação.",
  "notFound.title": "Página não encontrada",
  "notFound.body": "O endereço pode estar errado ou o item pode não estar mais acessível.",
};
